import type { Theme, ThemeOptions } from '@mui/material/styles';

const SQUARE_RADIUS_PX = '5px' as const;
const CIRCLE_RADIUS = '50%' as const;

const sizeMap = {
  small: { box: 25, icon: 14 },
  medium: { box: 34, icon: 18 },
  large: { box: 43, icon: 22 }
} as const;

function boxAndIcon(ownerState: { size?: string }) {
  const key = ownerState.size === 'small' || ownerState.size === 'large' ? ownerState.size : 'medium';
  return sizeMap[key];
}

const sharedRoot = {
  padding: 0,
  overflow: 'hidden',
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' }
} as const;

const secondaryTextColor = '#555F6C';
const secondarySurfaceColor = '#F2F4F9';

/**
 * MUI overrides for the design-system IconButton (all sizes).
 * Touch target and inner icon size align with the design-system Button icon sizing (16px at `medium`).
 */
export function iconButtonThemeComponents(theme: Theme): ThemeOptions['components'] {
  return {
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const { box, icon } = boxAndIcon(ownerState);
          return {
            ...sharedRoot,
            width: box,
            height: box,
            '& .MuiSvgIcon-root': {
              fontSize: icon,
              width: icon,
              height: icon
            },

            // shape
            '&.SmIconButton-shape-square': {
              borderRadius: SQUARE_RADIUS_PX,
              '& .MuiTouchRipple-root': {
                borderRadius: SQUARE_RADIUS_PX
              },
              '& .MuiTouchRipple-ripple, & .MuiTouchRipple-child': {
                borderRadius: SQUARE_RADIUS_PX
              }
            },
            '&.SmIconButton-shape-circle': {
              borderRadius: CIRCLE_RADIUS,
              '& .MuiTouchRipple-root': {
                borderRadius: CIRCLE_RADIUS
              },
              '& .MuiTouchRipple-ripple, & .MuiTouchRipple-child': {
                borderRadius: CIRCLE_RADIUS
              }
            },

            // contained
            '&.SmIconButton-variant-contained': {
              '&.SmIconButton-color-primary': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark
                }
              },
              '&.SmIconButton-color-secondary': {
                backgroundColor: secondarySurfaceColor,
                color: secondaryTextColor,
                '&:hover': {
                  backgroundColor: secondarySurfaceColor
                }
              },
              '&.SmIconButton-color-warning': {
                backgroundColor: theme.palette.warning.main,
                color: theme.palette.common.white,
                '&:hover': {
                  backgroundColor: theme.palette.warning.dark
                }
              },
              '&.SmIconButton-color-info': {
                backgroundColor: theme.palette.info.main,
                color: theme.palette.common.white,
                '&:hover': {
                  backgroundColor: theme.palette.info.dark
                }
              }
            },

            // outlined
            '&.SmIconButton-variant-outlined': {
              backgroundColor: 'transparent',
              border: '1px solid',
              '&.SmIconButton-color-primary': {
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              },
              '&.SmIconButton-color-secondary': {
                borderColor: theme.palette.divider,
                color: secondaryTextColor,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              },
              '&.SmIconButton-color-warning': {
                borderColor: theme.palette.warning.main,
                color: theme.palette.warning.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              },
              '&.SmIconButton-color-info': {
                borderColor: theme.palette.info.main,
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }
            },

            // text
            '&.SmIconButton-variant-text': {
              backgroundColor: 'transparent',
              '&.SmIconButton-color-primary': {
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              },
              '&.SmIconButton-color-secondary': {
                color: secondaryTextColor,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              },
              '&.SmIconButton-color-warning': {
                color: theme.palette.warning.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              },
              '&.SmIconButton-color-info': {
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }
            },
            '&.SmIconButton-variant-info': {
              backgroundColor: theme.palette.info.main,
              color: theme.palette.common.white,
              '&:hover': {
                backgroundColor: theme.palette.info.dark
              }
            }
          };
        }
      }
    }
  };
}
